import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/features/admin/api/adminApi';
import { LeadNotesForm } from '@/features/leads/components/LeadNotesForm';
import { ResetPasswordForm } from '@/features/admin/users/components/ResetPasswordForm';
import { Button } from '@/shared/components/Button';
import { ExportButton } from '@/shared/components/ExportButton';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { Pill } from '@/shared/components/Pill';
import { ApiError } from '@/shared/api/httpClient';
import type { ExhibitorDto, LeadDto, UserDto, UpsertLeadNoteRequest } from '@/shared/types/domain';

export function ExhibitorDetailPage() {
  const { exhibitorId } = useParams<{ exhibitorId: string }>();
  const navigate = useNavigate();
  const [exhibitor, setExhibitor] = useState<ExhibitorDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  function refetchExhibitor() {
    if (!exhibitorId) return;
    adminApi.exhibitors.getById(exhibitorId).then(setExhibitor);
  }

  useEffect(() => {
    if (!exhibitorId) return;
    setIsLoading(true);
    adminApi.exhibitors.getById(exhibitorId).then(setExhibitor).finally(() => setIsLoading(false));
  }, [exhibitorId]);

  if (isLoading) return <Spinner label="Loading exhibitor" />;
  if (!exhibitor || !exhibitorId) return <EmptyState icon="▦" title="Exhibitor not found" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <button
        type="button"
        onClick={() => navigate('/admin/exhibitors')}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', alignSelf: 'flex-start' }}
      >
        ← All exhibitors
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)' }}>
        <div className="stat-card">
          <span className="stat-card__value">{exhibitor.staffCount}</span>
          <span className="stat-card__label">Staff</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__value">{exhibitor.scanCount}</span>
          <span className="stat-card__label">Scans</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__value">{exhibitor.isActive ? 'Active' : 'Inactive'}</span>
          <span className="stat-card__label">Status</span>
        </div>
      </div>

      <ExhibitorProfileSection exhibitor={exhibitor} onSaved={refetchExhibitor} />
      <ExhibitorStaffSection exhibitorId={exhibitorId} />
      <ExhibitorLeadsSection exhibitorId={exhibitorId} />
    </div>
  );
}

// ---- Profile ----

function ExhibitorProfileSection({ exhibitor, onSaved }: { exhibitor: ExhibitorDto; onSaved: () => void }) {
  const [companyName, setCompanyName] = useState(exhibitor.companyName);
  const [logoUrl, setLogoUrl] = useState(exhibitor.logoUrl ?? '');
  const [brandColorHex, setBrandColorHex] = useState(exhibitor.brandColorHex ?? '#17D7C4');
  const [isActive, setIsActive] = useState(exhibitor.isActive);
  const [maxStaffSeats, setMaxStaffSeats] = useState(exhibitor.maxStaffSeats);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.exhibitors.update(exhibitor.id, {
        companyName,
        logoUrl: logoUrl || null,
        brandColorHex: brandColorHex || null,
        isActive,
        maxStaffSeats
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete ${exhibitor.companyName}? This cannot be undone from the UI.`)) return;
    setDeleteError(null);
    setIsDeleting(true);

    try {
      await adminApi.exhibitors.delete(exhibitor.id);
      navigate('/admin/exhibitors');
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.userMessage : 'Could not delete exhibitor.');
      setIsDeleting(false);
    }
  }

  return (
    <section>
      <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: 'var(--space-3)' }}>
        Profile
      </h2>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-4)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-5)'
        }}
      >
        {error && <div className="error-banner" style={{ gridColumn: '1 / -1' }}>{error}</div>}

        <div className="field">
          <label className="field__label">Company name</label>
          <input className="field__input" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label">Logo URL</label>
          <input className="field__input" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label">Brand colour</label>
          <input
            type="color"
            className="field__input"
            style={{ width: 64, padding: 4 }}
            value={brandColorHex}
            onChange={(e) => setBrandColorHex(e.target.value)}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>
        <div className="field">
          <label className="field__label">Staff seat limit</label>
          <input
            type="number"
            className="field__input"
            min={1}
            required
            value={maxStaffSeats}
            onChange={(e) => setMaxStaffSeats(Math.max(1, Number(e.target.value)))}
          />
        </div>

        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button type="submit" isLoading={isSaving}>
            Save changes
          </Button>
          <Button type="button" variant="danger" onClick={() => void handleDelete()} isLoading={isDeleting}>
            Delete exhibitor
          </Button>
        </div>
        {deleteError && <div className="error-banner" style={{ gridColumn: '1 / -1' }}>{deleteError}</div>}
      </form>
    </section>
  );
}

// ---- Staff ----

function ExhibitorStaffSection({ exhibitorId }: { exhibitorId: string }) {
  const [staff, setStaff] = useState<UserDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);

  function refetch() {
    setIsLoading(true);
    adminApi.users.listByExhibitor(exhibitorId).then(setStaff).finally(() => setIsLoading(false));
  }

  useEffect(refetch, [exhibitorId]);

  async function handleDelete(userId: string) {
    if (!window.confirm('Remove this staff member?')) return;
    await adminApi.users.delete(userId);
    refetch();
  }

  return (
    <section>
      <div className="admin-page__header">
        <h2 className="font-display" style={{ fontSize: '1.125rem', margin: 0 }}>
          Staff
        </h2>
        <Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? 'Cancel' : '+ Add staff'}</Button>
      </div>

      {showCreate && (
        <CreateStaffForm
          exhibitorId={exhibitorId}
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {isLoading ? (
        <Spinner label="Loading staff" />
      ) : staff.length === 0 ? (
        <EmptyState icon="◎" title="No staff yet" description="Add the first staff account above." />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {staff.map((u) => (
              <>
                <tr key={u.id}>
                  <td>
                    {u.firstName} {u.lastName}
                  </td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>
                    <Pill tone={u.isActive ? 'online' : 'offline'}>{u.isActive ? 'Active' : 'Disabled'}</Pill>
                  </td>
                  <td style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(editingId === u.id ? null : u.id);
                        setResettingId(null);
                      }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}
                    >
                      {editingId === u.id ? 'Close' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setResettingId(resettingId === u.id ? null : u.id);
                        setEditingId(null);
                      }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}
                    >
                      {resettingId === u.id ? 'Close' : 'Reset password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(u.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {editingId === u.id && (
                  <tr key={`${u.id}-edit`}>
                    <td colSpan={5} style={{ background: 'var(--bg)' }}>
                      <EditStaffForm
                        user={u}
                        onSaved={() => {
                          setEditingId(null);
                          refetch();
                        }}
                      />
                    </td>
                  </tr>
                )}
                {resettingId === u.id && (
                  <tr key={`${u.id}-reset`}>
                    <td colSpan={5} style={{ background: 'var(--bg)' }}>
                      <ResetPasswordForm userId={u.id} onDone={() => setResettingId(null)} />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CreateStaffForm({ exhibitorId, onCreated }: { exhibitorId: string; onCreated: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'ExhibitorAdmin' | 'ExhibitorStaff'>('ExhibitorStaff');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.users.create({ email, firstName, lastName, password, role, exhibitorId });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not create user.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-4)'
      }}
    >
      {error && <div className="error-banner" style={{ gridColumn: '1 / -1' }}>{error}</div>}
      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Email</label>
        <input type="email" className="field__input" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Temporary password</label>
        <input
          type="text"
          className="field__input"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="field__label">Role</label>
        <select className="field__input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="ExhibitorStaff">Exhibitor Staff</option>
          <option value="ExhibitorAdmin">Exhibitor Admin</option>
        </select>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <Button type="submit" isLoading={isSaving}>
          Create account
        </Button>
      </div>
    </form>
  );
}

function EditStaffForm({ user, onSaved }: { user: UserDto; onSaved: () => void }) {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [role, setRole] = useState(user.role as 'ExhibitorAdmin' | 'ExhibitorStaff');
  const [isActive, setIsActive] = useState(user.isActive);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.users.update(user.id, { firstName, lastName, role, isActive });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', padding: 'var(--space-3) 0', flexWrap: 'wrap' }}>
      {error && <div className="error-banner" style={{ flexBasis: '100%' }}>{error}</div>}
      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Role</label>
        <select className="field__input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="ExhibitorStaff">Exhibitor Staff</option>
          <option value="ExhibitorAdmin">Exhibitor Admin</option>
        </select>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      <Button type="submit" isLoading={isSaving}>
        Save
      </Button>
    </form>
  );
}

// ---- Leads/Scans ----

function ExhibitorLeadsSection({ exhibitorId }: { exhibitorId: string }) {
  const [leads, setLeads] = useState<LeadDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingScanId, setEditingScanId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport(format: 'csv' | 'xlsx') {
    setExportError(null);
    try {
      await (format === 'csv' ? adminApi.leads.exportCsv(exhibitorId) : adminApi.leads.exportXlsx(exhibitorId));
    } catch (err) {
      setExportError(err instanceof ApiError ? err.userMessage : `Could not export ${format.toUpperCase()}.`);
    }
  }

  function refetch() {
    setIsLoading(true);
    adminApi.leads.listByExhibitor(exhibitorId).then(setLeads).finally(() => setIsLoading(false));
  }

  useEffect(refetch, [exhibitorId]);

  async function handleDelete(scanId: string) {
    if (!window.confirm('Delete this lead? This cannot be undone from the UI.')) return;
    await adminApi.leads.delete(scanId);
    refetch();
  }

  async function handleSaveNotes(scanId: string, data: UpsertLeadNoteRequest) {
    await adminApi.leads.upsertNotes(scanId, data);
    refetch();
  }

  return (
    <section>
      <div className="admin-page__header">
        <h2 className="font-display" style={{ fontSize: '1.125rem', margin: 0 }}>
          Scans / Leads
        </h2>
        <ExportButton onExport={handleExport} disabled={leads.length === 0} />
      </div>

      {exportError && <div className="error-banner" style={{ marginBottom: 'var(--space-4)' }}>{exportError}</div>}

      {isLoading ? (
        <Spinner label="Loading leads" />
      ) : leads.length === 0 ? (
        <EmptyState icon="☰" title="No leads captured yet" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Badge ID</th>
              <th>Rating</th>
              <th>Scanned</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <>
                <tr key={lead.scanId}>
                  <td>{lead.consentGiven ? `${lead.firstName} ${lead.lastName}` : 'No consent'}</td>
                  <td className="font-mono">{lead.badgeId}</td>
                  <td>{lead.rating ?? '—'}</td>
                  <td>{new Date(lead.scannedAtUtc).toLocaleString()}</td>
                  <td style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <button
                      type="button"
                      onClick={() => setEditingScanId(editingScanId === lead.scanId ? null : lead.scanId)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}
                    >
                      {editingScanId === lead.scanId ? 'Close' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(lead.scanId)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {editingScanId === lead.scanId && (
                  <tr key={`${lead.scanId}-edit`}>
                    <td colSpan={5} style={{ background: 'var(--bg)', padding: 'var(--space-4)' }}>
                      <LeadNotesForm lead={lead} onSave={(data) => handleSaveNotes(lead.scanId, data)} />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}